import { MouseEvent, PropsWithChildren, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useErrorBoundary } from '@treams/treams-hooks';
import {
	FlexGap,
	GraphQLErrorMessage,
	Heading3,
	IconButton,
	JunkIcon,
	PencilIcon,
	UndoIcon,
	tokens,
} from '@treams/treams-ui';

const Row = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: ${tokens.gap.element}px;
	padding: ${tokens.padding.container}px;
	border-bottom: 1px solid ${tokens.border.subtle};

	&:last-child {
		border-bottom: 0;
	}
`;

const TitleLink = styled(Link)`
	min-width: 0;
	color: ${tokens.text.primary};
	text-decoration: none;

	&:hover {
		text-decoration: underline;
	}
`;

const TitleButton = styled.button`
	min-width: 0;
	padding: 0;
	border: 0;
	background: none;
	color: ${tokens.text.primary};
	text-align: left;
	cursor: pointer;

	&:hover {
		text-decoration: underline;
	}
`;

const TitleText = styled(Heading3)`
	margin: 0;
`;

export default function SettingsItem<T extends { id: string; isArchived: boolean }>(
	props: PropsWithChildren<{
		item: T;
		accessory?: ReactNode;
		link?: (item: T) => string;
		title: (item: T) => string;
		titleBadge?: ReactNode;
		archive?: [
			(options: { variables: { input: { id: string } } }) => Promise<void>,
			{ loading: boolean },
		];
		restore?: [
			(options: { variables: { input: { id: string } } }) => Promise<void>,
			{ loading: boolean },
		];
		loading?: boolean;
		onEdit?: () => void;
		onArchive?: () => void;
		onRefresh: () => void;
	}>,
) {
	const isArchived = !!props.item?.isArchived;
	const [error, attempt] = useErrorBoundary();
	const loading = props.archive?.[1].loading || props.restore?.[1].loading || props.loading;

	const onRestore =
		props.restore &&
		attempt(async () => {
			await props.restore?.[0]({
				variables: {
					input: {
						id: props.item!.id,
					},
				},
			});

			props.onRefresh();
		});

	const onDelete =
		props.archive &&
		attempt(async () => {
			await props.archive?.[0]({
				variables: {
					input: {
						id: props.item!.id,
					},
				},
			});

			props.onRefresh();
		});

	const stop = (event: MouseEvent) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const title = <TitleText>{props.title(props.item)}</TitleText>;

	return (
		<Row data-list-item>
			<FlexGap flexDirection="column" gap={tokens.gap.inline} minWidth={0} flexGrow={1}>
				<GraphQLErrorMessage error={error} />
				<FlexGap alignItems="center" gap={tokens.gap.element} minWidth={0}>
					{props.link ? (
						<TitleLink to={props.link(props.item)}>{title}</TitleLink>
					) : props.onEdit ? (
						<TitleButton type="button" onClick={props.onEdit}>
							{title}
						</TitleButton>
					) : (
						title
					)}
					{props.titleBadge}
					{props.accessory}
				</FlexGap>
				{props.children}
			</FlexGap>
			<FlexGap alignItems="center" gap={tokens.gap.element} flexShrink={0}>
				{props.onEdit && (
					<IconButton
						type="secondary"
						icon={<PencilIcon />}
						disabled={loading}
						onClick={(event) => {
							stop(event);
							props.onEdit?.();
						}}
					/>
				)}
				{isArchived
					? onRestore && (
							<IconButton
								type="secondary"
								icon={<UndoIcon />}
								disabled={loading}
								onClick={(event) => {
									stop(event);
									onRestore();
								}}
							/>
						)
					: (props.onArchive || onDelete) && (
							<IconButton
								type="destructive"
								icon={<JunkIcon />}
								disabled={loading}
								onClick={(event) => {
									stop(event);
									(props.onArchive ?? onDelete)?.();
								}}
							/>
						)}
			</FlexGap>
		</Row>
	);
}
